import React, { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import { CalendarClock, Check, ExternalLink, X } from 'lucide-react';
import mapi from './api';
import { useMobile } from './MobileContext';
import { MobileShell, MobileHeader, C, FilterChips, AppBottomNav } from './MobileShell';
import { isStaffArea } from './areas';
import { formatTime } from '@/lib/utils';

const WEEKDAYS = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];

const dateOf = (iso) => {
  const d = new Date(`${String(iso || '').slice(0, 10)}T12:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
};

const fmtSlot = (it) => {
  const d = dateOf(it.date);
  if (!d) return 'Termin offen';
  const p = (n) => String(n).padStart(2, '0');
  const day = `${WEEKDAYS[d.getDay()]}, ${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()}`;
  const time = it.start_time ? ` · ${formatTime(it.start_time)} Uhr` : '';
  const dur = it.duration_minutes ? ` · ${it.duration_minutes} Min` : '';
  return `${day}${time}${dur}`;
};

/**
 * Offene Terminanfragen in der Lehrerapp.
 *
 * Bestätigen und Ablehnen laufen über dieselben Token-Endpunkte wie die Buttons in der
 * Einladungsmail. Für Alternativvorschläge, die die Verfügbarkeit der Lehrkraft brauchen,
 * öffnet die App die vollständige Magic-Link-Seite.
 */
export default function MobileSlotRequests() {
  const { me, loading: meLoading, area, teacherId } = useMobile();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyToken, setBusyToken] = useState('');
  const [filter, setFilter] = useState('offen');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { scope: 'all' };
      if (teacherId) params.teacher_id = teacherId;
      const r = await mapi.get('/teacher/slot-requests', { params });
      setItems(r.data?.items || []);
    } catch (e) {
      const msg = e?.response?.data?.detail;
      setError(typeof msg === 'string' ? msg : 'Terminanfragen konnten nicht geladen werden');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [teacherId]);

  useEffect(() => {
    if (me && isStaffArea(area)) load();
  }, [me, area, load]);

  if (meLoading) {
    return <MobileShell><div className="flex-1 flex items-center justify-center" style={{ color: C.dim }}>Laden…</div></MobileShell>;
  }
  if (!me) return <Navigate to="/m/login" replace />;
  if (!isStaffArea(area)) return <Navigate to="/m" replace />;

  const statusOf = (it) => it.status || 'pending';
  const buckets = {
    offen: items.filter((it) => statusOf(it) === 'pending'),
    angenommen: items.filter((it) => statusOf(it) === 'confirmed'),
    abgelehnt: items.filter((it) => statusOf(it) === 'declined'),
    archiv: items.filter((it) => statusOf(it) === 'proposed_alternatives'),
  };
  const shown = buckets[filter] || [];

  const act = async (token, kind) => {
    if (kind === 'decline') {
      const reason = window.prompt('Grund für die Ablehnung (optional)');
      if (reason === null) return;
      setBusyToken(token);
      try {
        const r = await mapi.post(`/public/registrations/teacher-slot/${encodeURIComponent(token)}/decline`,
          { reason: reason.trim() || null });
        toast.success(r.data?.message || 'Rückmeldung gesendet');
        load();
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Ablehnen fehlgeschlagen');
      } finally {
        setBusyToken('');
      }
      return;
    }
    setBusyToken(token);
    try {
      const r = await mapi.post(`/public/registrations/teacher-slot/${encodeURIComponent(token)}/confirm`);
      toast.success(r.data?.message || 'Termin bestätigt');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bestätigen fehlgeschlagen');
    } finally {
      setBusyToken('');
    }
  };

  return (
    <MobileShell bottomNav={<AppBottomNav />}>
      <MobileHeader title="Terminanfragen" backTo="/m" onRefresh={load} />
      <FilterChips
        testIdPrefix="mobile-slot-filter"
        value={filter}
        onChange={setFilter}
        items={[
          { k: 'offen', label: `Offen (${buckets.offen.length})` },
          { k: 'angenommen', label: 'Angenommen' },
          { k: 'abgelehnt', label: 'Abgelehnt' },
          { k: 'archiv', label: 'Archiv' },
        ]}
      />
      <div className="px-4 py-4 space-y-4 pb-8" data-testid="mobile-slot-requests-page">
        <p className="text-xs" style={{ color: C.dim }}>
          Die Verwaltung bittet um Rückmeldung zu diesen Terminen aus der Online-Anmeldung.
        </p>

        {loading ? (
          <div className="py-10 text-center text-sm" style={{ color: C.dim }} data-testid="mobile-slot-requests-loading">
            Laden…
          </div>
        ) : error ? (
          <div className="px-4 py-6 text-sm text-center" style={{ color: '#fca5a5' }} data-testid="mobile-slot-requests-error">
            {error}
          </div>
        ) : shown.length === 0 ? (
          <div className="py-10 text-center text-sm" style={{ color: C.dim }} data-testid="mobile-slot-requests-empty">
            {filter === 'offen' ? 'Keine offenen Terminanfragen' : 'Keine Vorgänge in dieser Ansicht'}
          </div>
        ) : (
          <ul className="space-y-3" data-testid="mobile-slot-requests-list">
            {shown.map((it) => (
              <li key={it.token} className="rounded-xl p-4"
                  style={{ background: C.card, border: `1px solid ${C.border}` }}
                  data-testid={`mobile-slot-request-${it.registration_id}`}>
                <div className="flex items-start gap-2">
                  <CalendarClock className="w-4 h-4 mt-0.5 shrink-0" style={{ color: C.red }} />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate">
                      {it.student_name}
                      {it.instrument ? <span className="font-normal" style={{ color: C.dim }}> · {it.instrument}</span> : null}
                    </p>
                    <p className="text-xs mt-0.5" style={{ color: C.dim }}>{fmtSlot(it)}</p>
                  </div>
                </div>
                {statusOf(it) === 'pending' && (
                <>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <button onClick={() => act(it.token, 'confirm')} disabled={busyToken === it.token}
                          className="py-2.5 rounded-lg text-sm font-semibold text-white flex items-center justify-center gap-1.5 disabled:opacity-50"
                          style={{ background: C.success }}
                          data-testid={`mobile-slot-request-confirm-${it.registration_id}`}>
                    <Check className="w-4 h-4" /> Passt
                  </button>
                  <button onClick={() => act(it.token, 'decline')} disabled={busyToken === it.token}
                          className="py-2.5 rounded-lg text-sm font-semibold flex items-center justify-center gap-1.5 disabled:opacity-50"
                          style={{ background: C.bg, border: `1px solid ${C.border}`, color: C.text }}
                          data-testid={`mobile-slot-request-decline-${it.registration_id}`}>
                    <X className="w-4 h-4" /> Passt nicht
                  </button>
                </div>
                <a href={`/teacher-slot/${encodeURIComponent(it.token)}`}
                   className="mt-2 flex items-center justify-center gap-1.5 py-2 text-xs"
                   style={{ color: C.dim }}
                   data-testid={`mobile-slot-request-alternatives-${it.registration_id}`}>
                  <ExternalLink className="w-3.5 h-3.5" /> Alternativtermine vorschlagen
                </a>
                </>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </MobileShell>
  );
}
